import type { AgentPublicConfig, Cta } from '@cloneai/shared-types';
import { Play, Square } from 'lucide-react';
import {
  useCallback,
  useEffect,
  useRef,
  useState,
  type CSSProperties,
  type Dispatch,
  type FormEvent,
  type JSX,
} from 'react';

import type { RuntimeLabels } from '../language.js';
import type {
  AnimationPlaybackFailure,
  QueuedAnimationController,
} from '../hooks/useQueuedAnimation.js';
import type { TextChatState } from '../hooks/useTextChat.js';
import type { AgentEvent, AgentState } from '../state.js';
import type { VoiceSession } from '../voice/VoiceSession.js';
import { CtaOverlay } from './CtaOverlay.js';
import { SubtitlePanel, type SubtitleLine } from './SubtitlePanel.js';

interface AgentStageProps {
  config: AgentPublicConfig;
  state: AgentState;
  dispatch: Dispatch<AgentEvent>;
  /**
   * Active voice session (P2-08 / P2-09). In P2-10 this is always `null`; the
   * subtitle and speaking-overlay wiring activate once a real session is
   * handed in.
   */
  voiceSession?: VoiceSession | null;
  selectedLang: string;
  languages: readonly string[];
  labels: RuntimeLabels;
  textChat: TextChatState;
  animation: QueuedAnimationController;
  onLanguageChange: (lang: string) => void;
  onRuntimeStart: (details: { hasVideo: boolean }) => void;
  onSwitchToText: () => void;
}

function reportPlaybackFailure(message: string, error: unknown): void {
  if (
    typeof error === 'object' &&
    error !== null &&
    'name' in error &&
    error.name === 'AbortError'
  ) {
    // play() rejects with AbortError when a newer pause/load request wins the
    // media race. That is expected lifecycle control, not an application error.
    return;
  }
  console.error(message, error);
}

/**
 * Lifecycle-aware video player.
 *
 * Three stacked <video> elements drive the visuals:
 *   - Main intro video: unmuted, plays once on Start, fires VIDEO_END on ended.
 *   - Speaking overlay: muted, appears while the agent is speaking.
 *     Reset to 0 every 15 s (muted-loop trick).
 *   - Rendered response: unmuted and only revealed after playback starts.
 *
 * The source layer is hidden whenever either response layer is visible. This
 * prevents transparent avatar videos from compositing over a paused duplicate
 * of the same person instead of directly over the configured background.
 *
 * The component reads agent speech state from the optional `voiceSession`
 * prop; until P2-08/P2-09 wire in a real session, the overlay stays hidden.
 */
export function AgentStage({
  config,
  state,
  dispatch,
  voiceSession = null,
  selectedLang,
  languages,
  labels,
  textChat,
  animation,
  onLanguageChange,
  onRuntimeStart,
  onSwitchToText,
}: AgentStageProps): JSX.Element {
  const backgroundStyle = resolveBackground(config);
  const accentColor = config.brand.brandColor ?? '#646cff';

  const mainVideoRef = useRef<HTMLVideoElement>(null);
  const overlayVideoRef = useRef<HTMLVideoElement>(null);
  const animationVideoRef = useRef<HTMLVideoElement>(null);
  const attemptedAnimationIdRef = useRef<string | null>(null);
  const terminalAnimationIdRef = useRef<string | null>(null);
  const loadTimerRef = useRef<ReturnType<typeof setTimeout> | null>(null);
  const playbackTimerRef = useRef<ReturnType<typeof setTimeout> | null>(null);

  const [hasStarted, setHasStarted] = useState<boolean>(false);
  const [isAgentSpeaking, setIsAgentSpeaking] = useState<boolean>(false);
  const [subtitleLines, setSubtitleLines] = useState<SubtitleLine[]>([]);
  const nextSubtitleIdRef = useRef(0);
  const [isAnimationVisible, setIsAnimationVisible] = useState<boolean>(false);
  const [needsAnimationGesture, setNeedsAnimationGesture] = useState<boolean>(false);

  const animationMedia = animation.media;
  const hasVideo = Boolean(config.videoUrl || config.hevcVideoUrl);

  const clearLoadTimer = useCallback((): void => {
    if (loadTimerRef.current === null) return;
    clearTimeout(loadTimerRef.current);
    loadTimerRef.current = null;
  }, []);

  const clearPlaybackTimer = useCallback((): void => {
    if (playbackTimerRef.current === null) return;
    clearTimeout(playbackTimerRef.current);
    playbackTimerRef.current = null;
  }, []);

  const failAnimation = useCallback(
    (animationId: string, reason: AnimationPlaybackFailure): void => {
      if (
        animationMedia?.animationId !== animationId ||
        terminalAnimationIdRef.current === animationId
      ) {
        return;
      }
      terminalAnimationIdRef.current = animationId;
      clearLoadTimer();
      clearPlaybackTimer();
      setIsAnimationVisible(false);
      setNeedsAnimationGesture(false);
      animation.onMediaFailure(animationId, reason);
    },
    [animation.onMediaFailure, animationMedia?.animationId, clearLoadTimer, clearPlaybackTimer],
  );

  const tryAnimationPlayback = useCallback((): void => {
    const video = animationVideoRef.current;
    if (!video || !animationMedia) return;
    if (terminalAnimationIdRef.current === animationMedia.animationId) return;
    const expiresAtMs = Date.parse(animationMedia.expiresAt);
    if (!Number.isFinite(expiresAtMs) || expiresAtMs <= Date.now() + 1_000) {
      failAnimation(animationMedia.animationId, 'media-expired');
      return;
    }

    attemptedAnimationIdRef.current = animationMedia.animationId;
    clearPlaybackTimer();
    const animationId = animationMedia.animationId;
    playbackTimerRef.current = setTimeout(() => {
      playbackTimerRef.current = null;
      failAnimation(animationId, 'media-stalled');
    }, 15_000);
    const playPromise = video.play();
    if (!playPromise || typeof playPromise.catch !== 'function') return;
    playPromise.catch((error: unknown) => {
      if (
        animationMedia.animationId !== animation.media?.animationId ||
        terminalAnimationIdRef.current === animationMedia.animationId
      ) {
        return;
      }
      clearPlaybackTimer();
      if (
        typeof error === 'object' &&
        error !== null &&
        'name' in error &&
        error.name === 'NotAllowedError'
      ) {
        setNeedsAnimationGesture(true);
        return;
      }
      failAnimation(animationMedia.animationId, 'autoplay');
    });
  }, [animation.media?.animationId, animationMedia, clearPlaybackTimer, failAnimation]);

  useEffect(() => {
    attemptedAnimationIdRef.current = null;
    terminalAnimationIdRef.current = null;
    clearLoadTimer();
    clearPlaybackTimer();
    setIsAnimationVisible(false);
    setNeedsAnimationGesture(false);
  }, [animationMedia?.animationId, clearLoadTimer, clearPlaybackTimer]);

  useEffect(() => {
    if (!animationMedia || animation.phase !== 'media-ready') return undefined;
    const animationId = animationMedia.animationId;
    clearLoadTimer();
    loadTimerRef.current = setTimeout(() => {
      loadTimerRef.current = null;
      failAnimation(animationId, 'media-timeout');
    }, 15_000);
    return clearLoadTimer;
  }, [animation.phase, animationMedia, clearLoadTimer, failAnimation]);

  useEffect(
    () => () => {
      clearLoadTimer();
      clearPlaybackTimer();
    },
    [clearLoadTimer, clearPlaybackTimer],
  );

  useEffect(() => {
    if (state.status !== 'ready') return;
    setHasStarted(false);
    setIsAgentSpeaking(false);
    setSubtitleLines([]);

    const main = mainVideoRef.current;
    if (main) {
      if (!main.paused) main.pause();
      if (main.currentTime !== 0) main.currentTime = 0;
    }
    const overlay = overlayVideoRef.current;
    if (overlay) {
      if (!overlay.paused) overlay.pause();
      if (overlay.currentTime !== 0) overlay.currentTime = 0;
    }
  }, [state.status]);

  // Wire voice-session callbacks → local UI state.
  useEffect(() => {
    if (!voiceSession) return;
    voiceSession.onAgentSpeechStart = (): void => setIsAgentSpeaking(true);
    voiceSession.onAgentSpeechEnd = (): void => setIsAgentSpeaking(false);
    voiceSession.onTranscript = (text: string): void => {
      const line = { id: nextSubtitleIdRef.current++, text };
      setSubtitleLines((prev) => [...prev.slice(-4), line]);
    };
    return () => {
      delete voiceSession.onAgentSpeechStart;
      delete voiceSession.onAgentSpeechEnd;
      delete voiceSession.onTranscript;
    };
  }, [voiceSession]);

  // Speaking-overlay cross-fade: play/reset based on `isAgentSpeaking`.
  useEffect(() => {
    const overlay = overlayVideoRef.current;
    if (!overlay) return;
    if (isAgentSpeaking) {
      overlay.currentTime = 0;
      const p = overlay.play();
      if (p && typeof p.catch === 'function') {
        p.catch((err: unknown) => {
          reportPlaybackFailure('Speaking overlay playback failed:', err);
        });
      }
    } else {
      if (!overlay.paused) overlay.pause();
      if (overlay.currentTime !== 0) overlay.currentTime = 0;
    }
  }, [isAgentSpeaking]);

  // 15s loop-reset trick on the overlay video.
  const handleOverlayTimeUpdate = useCallback((): void => {
    const vid = overlayVideoRef.current;
    if (!vid || !isAgentSpeaking) return;
    if (vid.currentTime >= 15) {
      vid.currentTime = 0;
      const p = vid.play();
      if (p && typeof p.catch === 'function') {
        p.catch((err: unknown) => {
          reportPlaybackFailure('Overlay loop reset failed:', err);
        });
      }
    }
  }, [isAgentSpeaking]);

  const handleAnimationCanPlay = useCallback((): void => {
    if (!animationMedia) return;
    clearLoadTimer();
    if (attemptedAnimationIdRef.current === animationMedia.animationId) return;
    tryAnimationPlayback();
  }, [animationMedia, clearLoadTimer, tryAnimationPlayback]);

  const handleAnimationPlaying = useCallback((): void => {
    if (!animationMedia || terminalAnimationIdRef.current === animationMedia.animationId) return;
    clearLoadTimer();
    clearPlaybackTimer();
    setNeedsAnimationGesture(false);
    setIsAnimationVisible(true);
    setIsAgentSpeaking(false);
    const main = mainVideoRef.current;
    if (main && !main.paused) main.pause();
    const overlay = overlayVideoRef.current;
    if (overlay && !overlay.paused) overlay.pause();
    animation.onPlaying(animationMedia.animationId);
  }, [animation.onPlaying, animationMedia, clearLoadTimer, clearPlaybackTimer]);

  const handleAnimationEnded = useCallback((): void => {
    if (!animationMedia || terminalAnimationIdRef.current === animationMedia.animationId) return;
    terminalAnimationIdRef.current = animationMedia.animationId;
    clearLoadTimer();
    clearPlaybackTimer();
    setIsAnimationVisible(false);
    setNeedsAnimationGesture(false);
    animation.onEnded(animationMedia.animationId);
  }, [animation.onEnded, animationMedia, clearLoadTimer, clearPlaybackTimer]);

  const handleAnimationWaiting = useCallback((): void => {
    if (!animationMedia || terminalAnimationIdRef.current === animationMedia.animationId) return;
    clearPlaybackTimer();
    const animationId = animationMedia.animationId;
    playbackTimerRef.current = setTimeout(() => {
      playbackTimerRef.current = null;
      failAnimation(animationId, 'media-stalled');
    }, 8_000);
  }, [animationMedia, clearPlaybackTimer, failAnimation]);

  const handleAnimationProgress = useCallback((): void => {
    clearPlaybackTimer();
  }, [clearPlaybackTimer]);

  const handleAnimationError = useCallback((): void => {
    if (animationMedia) failAnimation(animationMedia.animationId, 'media-error');
  }, [animationMedia, failAnimation]);

  const handleAnimationAbort = useCallback((): void => {
    if (animationMedia) failAnimation(animationMedia.animationId, 'media-aborted');
  }, [animationMedia, failAnimation]);

  const handleAnimationGesture = useCallback((): void => {
    attemptedAnimationIdRef.current = null;
    setNeedsAnimationGesture(false);
    tryAnimationPlayback();
  }, [tryAnimationPlayback]);

  const handleAnimationReplay = useCallback((): void => {
    const video = animationVideoRef.current;
    if (!video || !animationMedia || !animation.replay(animationMedia.animationId)) return;

    terminalAnimationIdRef.current = null;
    attemptedAnimationIdRef.current = animationMedia.animationId;
    clearLoadTimer();
    clearPlaybackTimer();
    setIsAnimationVisible(false);
    setNeedsAnimationGesture(false);
    video.currentTime = 0;
    tryAnimationPlayback();
  }, [animation.replay, animationMedia, clearLoadTimer, clearPlaybackTimer, tryAnimationPlayback]);

  const handleMainVideoEnded = useCallback((): void => {
    dispatch({ type: 'VIDEO_END' });
  }, [dispatch]);

  const handleCtaClick = useCallback((cta: Cta): void => {
    window.dispatchEvent(new CustomEvent('cta-click', { detail: { ctaId: cta.id, url: cta.url } }));
  }, []);

  const handleStart = useCallback((): void => {
    setHasStarted(true);
    onRuntimeStart({ hasVideo });
    dispatch({ type: 'VIDEO_START' });

    if (!hasVideo) {
      // No intro video — treat as instantly ended so the state machine moves on.
      dispatch({ type: 'VIDEO_END' });
      return;
    }

    const main = mainVideoRef.current;
    if (main) {
      const p = main.play();
      if (p && typeof p.catch === 'function') {
        p.catch((err: unknown) => {
          reportPlaybackFailure('Main video playback failed:', err);
        });
      }
    }
  }, [dispatch, hasVideo, onRuntimeStart]);

  const showConnecting = state.status === 'minting-session';
  const showEnded = state.status === 'ended';
  const showTextFallback =
    config.interactionMode === 'voice' &&
    config.textChatFallbackEnabled &&
    (state.status === 'minting-session' || state.status === 'voice-active');
  const showTextChat = state.status === 'text-active';

  return (
    <>
      <div
        data-testid="agent-background"
        style={{ position: 'fixed', inset: 0, ...backgroundStyle, zIndex: 0 }}
      />
      <div
        style={{
          position: 'relative',
          zIndex: 1,
          width: '100%',
          height: '100%',
        }}
      >
        {hasVideo ? (
          <>
            <video
              ref={mainVideoRef}
              src={config.hevcVideoUrl ? undefined : (config.videoUrl ?? undefined)}
              playsInline
              onEnded={handleMainVideoEnded}
              aria-label="Agent introduction video"
              style={{
                position: 'absolute',
                inset: 0,
                width: '100%',
                height: '100%',
                objectFit: 'contain',
                background: 'transparent',
                opacity: isAgentSpeaking || isAnimationVisible ? 0 : 1,
                display: 'block',
              }}
            >
              <AgentVideoSources config={config} />
            </video>
            <video
              ref={overlayVideoRef}
              data-testid="speaking-overlay-video"
              src={config.hevcVideoUrl ? undefined : (config.videoUrl ?? undefined)}
              playsInline
              muted
              preload="auto"
              aria-hidden="true"
              onTimeUpdate={handleOverlayTimeUpdate}
              style={{
                position: 'absolute',
                inset: 0,
                width: '100%',
                height: '100%',
                objectFit: 'contain',
                background: 'transparent',
                opacity: isAgentSpeaking ? 1 : 0,
                pointerEvents: 'none',
              }}
            >
              <AgentVideoSources config={config} />
            </video>
          </>
        ) : (
          <div
            data-testid="video-placeholder"
            style={{
              position: 'absolute',
              inset: 0,
              background: 'transparent',
            }}
          />
        )}

        {animationMedia ? (
          <video
            key={animationMedia.animationId}
            ref={animationVideoRef}
            data-testid="animated-response-video"
            src={animationMedia.playbackUrl}
            playsInline
            preload="auto"
            aria-hidden="true"
            onCanPlay={handleAnimationCanPlay}
            onPlaying={handleAnimationPlaying}
            onEnded={handleAnimationEnded}
            onPause={handleAnimationWaiting}
            onWaiting={handleAnimationWaiting}
            onStalled={handleAnimationWaiting}
            onTimeUpdate={handleAnimationProgress}
            onError={handleAnimationError}
            onAbort={handleAnimationAbort}
            style={{
              position: 'absolute',
              inset: 0,
              zIndex: 2,
              width: '100%',
              height: '100%',
              objectFit: 'contain',
              background: 'transparent',
              opacity: isAnimationVisible ? 1 : 0,
              pointerEvents: 'none',
            }}
          />
        ) : null}

        <CtaOverlay ctas={config.ctas} videoRef={mainVideoRef} onCtaClick={handleCtaClick} />

        <BrandMark brand={config.brand} accentColor={accentColor} />

        <LanguageSelector
          languages={languages}
          selectedLang={selectedLang}
          label={labels.languageSelector}
          accentColor={accentColor}
          disabled={textChat.isInputBlocked}
          onChange={onLanguageChange}
        />

        {!hasStarted && state.status === 'ready' && (
          <StartControl
            label={labels.startSession}
            retryHint={state.retryHint ? labels.retryHint : null}
            accentColor={accentColor}
            onClick={handleStart}
          />
        )}

        {showConnecting && <ConnectingLabel label={labels.connecting} accentColor={accentColor} />}
        {/* E2E hooks: invisible markers for state-machine assertions. */}
        {state.status === 'minting-session' && <div data-testid="minting-session" hidden />}
        {state.status === 'voice-active' && <div data-testid="voice-active" hidden />}
        {state.status === 'text-active' && <div data-testid="text-active" hidden />}

        {showTextFallback && (
          <TextFallbackButton
            label={labels.switchToText}
            accentColor={accentColor}
            onClick={onSwitchToText}
          />
        )}

        {showTextChat && (
          <TextChatPanel
            labels={labels}
            accentColor={accentColor}
            messages={textChat.messages}
            isSending={textChat.isSending}
            isInputBlocked={textChat.isInputBlocked}
            error={textChat.error}
            animationAction={
              animation.phase === 'replay-ready' && animationMedia && !textChat.isSending
                ? {
                    kind: 'play',
                    label: labels.playAnimation,
                    onClick: handleAnimationReplay,
                  }
                : animation.isBusy
                  ? needsAnimationGesture && animationMedia
                    ? {
                        kind: 'play',
                        label: labels.playAnimation,
                        onClick: handleAnimationGesture,
                      }
                    : {
                        kind: 'stop',
                        label: labels.stopAnimation,
                        onClick: animation.cancel,
                      }
                  : null
            }
            onSend={textChat.sendMessage}
          />
        )}

        {showEnded && <EndedOverlay label={labels.ended} />}

        <SubtitlePanel lines={subtitleLines} label={labels.subtitles} />
      </div>
    </>
  );
}

function AgentVideoSources({ config }: { readonly config: AgentPublicConfig }): JSX.Element | null {
  if (!config.hevcVideoUrl) return null;

  return (
    <>
      <source src={config.hevcVideoUrl} type={'video/quicktime; codecs="hvc1"'} />
      {config.videoUrl ? <source src={config.videoUrl} /> : null}
    </>
  );
}

function LanguageSelector({
  languages,
  selectedLang,
  label,
  accentColor,
  disabled,
  onChange,
}: {
  languages: readonly string[];
  selectedLang: string;
  label: string;
  accentColor: string;
  disabled: boolean;
  onChange: (lang: string) => void;
}): JSX.Element | null {
  if (languages.length <= 1) return null;
  return (
    <select
      aria-label={label}
      value={selectedLang}
      disabled={disabled}
      onChange={(e) => onChange(e.currentTarget.value)}
      style={{
        position: 'absolute',
        top: 16,
        right: 16,
        zIndex: 3,
        height: 34,
        minWidth: 74,
        borderRadius: 6,
        border: `1px solid ${accentColor}`,
        background: 'rgba(0,0,0,0.55)',
        color: '#fff',
        fontSize: 13,
        lineHeight: 1,
        padding: '0 10px',
        textTransform: 'uppercase',
        cursor: disabled ? 'default' : 'pointer',
        opacity: disabled ? 0.55 : 1,
      }}
    >
      {languages.map((lang) => (
        <option key={lang} value={lang}>
          {lang.toUpperCase()}
        </option>
      ))}
    </select>
  );
}

function BrandMark({
  brand,
  accentColor,
}: {
  brand: AgentPublicConfig['brand'];
  accentColor: string;
}): JSX.Element {
  const initials = brand.name
    .split(/\s+/)
    .map((part) => part[0])
    .filter(Boolean)
    .slice(0, 2)
    .join('')
    .toUpperCase();

  return (
    <div
      aria-label={brand.name}
      style={{
        position: 'absolute',
        top: 16,
        left: 16,
        zIndex: 3,
        display: 'flex',
        alignItems: 'center',
        gap: 9,
        minHeight: 36,
        maxWidth: 'min(320px, 58vw)',
        padding: '5px 10px 5px 6px',
        borderRadius: 8,
        background: 'rgba(0,0,0,0.48)',
        color: '#fff',
        fontSize: 13,
        fontWeight: 600,
        letterSpacing: 0,
        pointerEvents: 'none',
      }}
    >
      {brand.logoUrl ? (
        <img
          src={brand.logoUrl}
          alt=""
          style={{
            width: 28,
            height: 28,
            borderRadius: 6,
            objectFit: 'cover',
            background: '#fff',
          }}
        />
      ) : (
        <span
          aria-hidden="true"
          style={{
            width: 28,
            height: 28,
            borderRadius: 6,
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            background: accentColor,
            color: '#fff',
            fontSize: 11,
            fontWeight: 700,
          }}
        >
          {initials || 'CA'}
        </span>
      )}
      <span
        style={{
          overflow: 'hidden',
          textOverflow: 'ellipsis',
          whiteSpace: 'nowrap',
        }}
      >
        {brand.name}
      </span>
    </div>
  );
}

function StartControl({
  label,
  retryHint,
  accentColor,
  testId = 'start-button',
  onClick,
}: {
  label: string;
  retryHint: string | null;
  accentColor: string;
  testId?: string;
  onClick: () => void;
}): JSX.Element {
  return (
    <div
      style={{
        position: 'absolute',
        top: '50%',
        left: '50%',
        transform: 'translate(-50%, -50%)',
        zIndex: 3,
        display: 'flex',
        flexDirection: 'column',
        alignItems: 'center',
        gap: 14,
      }}
    >
      {retryHint ? <RetryHint label={retryHint} /> : null}
      <StartButton label={label} accentColor={accentColor} testId={testId} onClick={onClick} />
    </div>
  );
}

function StartButton({
  label,
  accentColor,
  testId,
  onClick,
}: {
  label: string;
  accentColor: string;
  testId: string;
  onClick: () => void;
}): JSX.Element {
  return (
    <button
      type="button"
      onClick={onClick}
      aria-label={label}
      data-testid={testId}
      style={{
        width: 96,
        height: 96,
        borderRadius: '50%',
        border: 'none',
        background: 'rgba(255,255,255,0.85)',
        cursor: 'pointer',
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'center',
        padding: 0,
      }}
    >
      <svg
        width="64"
        height="64"
        viewBox="0 0 64 64"
        fill="none"
        xmlns="http://www.w3.org/2000/svg"
        aria-hidden="true"
      >
        <polygon points="26,20 50,32 26,44" fill={accentColor} />
      </svg>
    </button>
  );
}

function RetryHint({ label }: { label: string }): JSX.Element {
  return (
    <div
      role="status"
      style={{
        width: 'min(300px, calc(100vw - 48px))',
        padding: '8px 12px',
        borderRadius: 8,
        background: 'rgba(0,0,0,0.68)',
        color: '#fff',
        fontSize: 13,
        lineHeight: 1.4,
        textAlign: 'center',
        letterSpacing: 0,
      }}
    >
      {label}
    </div>
  );
}

function ConnectingLabel({
  label,
  accentColor,
}: {
  label: string;
  accentColor: string;
}): JSX.Element {
  return (
    <div
      role="status"
      style={{
        position: 'absolute',
        top: 16,
        left: '50%',
        transform: 'translateX(-50%)',
        padding: '6px 14px',
        borderRadius: 999,
        background: 'rgba(0,0,0,0.55)',
        border: `1px solid ${accentColor}`,
        color: '#fff',
        fontSize: 13,
        letterSpacing: 0,
        pointerEvents: 'none',
      }}
    >
      {label}
    </div>
  );
}

function EndedOverlay({ label }: { label: string }): JSX.Element {
  return (
    <div
      style={{
        position: 'absolute',
        inset: 0,
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'center',
        background: 'rgba(0,0,0,0.6)',
        color: '#fff',
        fontSize: 22,
        pointerEvents: 'none',
      }}
    >
      {label}
    </div>
  );
}

function TextFallbackButton({
  label,
  accentColor,
  onClick,
}: {
  label: string;
  accentColor: string;
  onClick: () => void;
}): JSX.Element {
  return (
    <button
      type="button"
      onClick={onClick}
      data-testid="text-fallback-button"
      style={{
        position: 'absolute',
        left: '50%',
        bottom: 82,
        transform: 'translateX(-50%)',
        zIndex: 12,
        minHeight: 30,
        maxWidth: 'calc(100vw - 48px)',
        border: `1px solid ${accentColor}`,
        borderRadius: 999,
        background: 'rgba(0,0,0,0.52)',
        color: '#fff',
        padding: '6px 12px',
        fontSize: 12,
        lineHeight: 1,
        letterSpacing: 0,
        cursor: 'pointer',
      }}
    >
      {label}
    </button>
  );
}

function TextChatPanel({
  labels,
  accentColor,
  messages,
  isSending,
  isInputBlocked,
  error,
  animationAction,
  onSend,
}: {
  labels: RuntimeLabels;
  accentColor: string;
  messages: TextChatState['messages'];
  isSending: boolean;
  isInputBlocked: boolean;
  error: string | null;
  animationAction: {
    readonly kind: 'play' | 'stop';
    readonly label: string;
    readonly onClick: () => void;
  } | null;
  onSend: (message: string) => Promise<void>;
}): JSX.Element {
  const [draft, setDraft] = useState('');
  const scrollRef = useRef<HTMLDivElement>(null);

  useEffect(() => {
    const el = scrollRef.current;
    if (!el) return;
    el.scrollTop = el.scrollHeight;
  }, [messages.length, isSending]);

  const submit = (event: FormEvent<HTMLFormElement>): void => {
    event.preventDefault();
    const message = draft.trim();
    if (!message || isInputBlocked) return;
    setDraft('');
    void onSend(message);
  };

  return (
    <section
      aria-label={labels.textChatTitle}
      data-testid="text-chat-panel"
      style={{
        position: 'absolute',
        right: 16,
        bottom: 16,
        zIndex: 11,
        width: 'min(420px, calc(100vw - 32px))',
        maxHeight: 'min(520px, calc(100vh - 112px))',
        display: 'grid',
        gridTemplateRows: 'auto minmax(96px, 1fr) auto',
        borderRadius: 8,
        border: `1px solid ${accentColor}`,
        background: 'rgba(8,10,14,0.86)',
        color: '#fff',
        overflow: 'hidden',
        boxShadow: '0 18px 48px rgba(0,0,0,0.34)',
      }}
    >
      <div
        style={{
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'space-between',
          gap: 8,
          padding: '10px 12px',
          borderBottom: '1px solid rgba(255,255,255,0.16)',
          fontSize: 13,
          fontWeight: 600,
        }}
      >
        <span>{labels.textChatTitle}</span>
        {animationAction ? (
          <button
            type="button"
            aria-label={animationAction.label}
            title={animationAction.label}
            data-testid={
              animationAction.kind === 'play' ? 'animation-play-button' : 'animation-stop-button'
            }
            onClick={animationAction.onClick}
            style={{
              width: 30,
              height: 30,
              flex: '0 0 30px',
              display: 'inline-flex',
              alignItems: 'center',
              justifyContent: 'center',
              border: `1px solid ${accentColor}`,
              borderRadius: 6,
              background: 'rgba(255,255,255,0.08)',
              color: '#fff',
              padding: 0,
              cursor: 'pointer',
            }}
          >
            {animationAction.kind === 'play' ? (
              <Play size={17} fill="currentColor" aria-hidden="true" />
            ) : (
              <Square size={14} fill="currentColor" aria-hidden="true" />
            )}
          </button>
        ) : null}
      </div>
      <div
        ref={scrollRef}
        aria-live="polite"
        style={{
          minHeight: 120,
          overflowY: 'auto',
          display: 'flex',
          flexDirection: 'column',
          gap: 8,
          padding: 12,
        }}
      >
        {messages.map((message) => (
          <div
            key={message.id}
            style={{
              alignSelf: message.role === 'user' ? 'flex-end' : 'flex-start',
              maxWidth: '86%',
              borderRadius: 8,
              padding: '8px 10px',
              background: message.role === 'user' ? accentColor : 'rgba(255,255,255,0.12)',
              color: '#fff',
              fontSize: 14,
              lineHeight: 1.35,
              whiteSpace: 'pre-wrap',
              overflowWrap: 'anywhere',
            }}
          >
            {message.text}
          </div>
        ))}
        {isSending ? (
          <div
            role="status"
            style={{
              alignSelf: 'flex-start',
              maxWidth: '86%',
              borderRadius: 8,
              padding: '8px 10px',
              background: 'rgba(255,255,255,0.12)',
              color: 'rgba(255,255,255,0.82)',
              fontSize: 13,
            }}
          >
            {labels.textChatThinking}
          </div>
        ) : null}
        {error ? (
          <div
            role="alert"
            style={{
              color: '#fecaca',
              fontSize: 12,
              lineHeight: 1.35,
            }}
          >
            {labels.textChatError}
          </div>
        ) : null}
      </div>
      <form
        onSubmit={submit}
        style={{
          display: 'grid',
          gridTemplateColumns: 'minmax(0, 1fr) auto',
          gap: 8,
          padding: 10,
          borderTop: '1px solid rgba(255,255,255,0.16)',
        }}
      >
        <input
          value={draft}
          onChange={(event) => setDraft(event.currentTarget.value)}
          placeholder={labels.textChatPlaceholder}
          aria-label={labels.textChatPlaceholder}
          style={{
            minWidth: 0,
            height: 38,
            borderRadius: 6,
            border: '1px solid rgba(255,255,255,0.18)',
            background: 'rgba(255,255,255,0.08)',
            color: '#fff',
            padding: '0 10px',
            fontSize: 14,
          }}
        />
        <button
          type="submit"
          disabled={draft.trim().length === 0 || isInputBlocked}
          style={{
            minWidth: 72,
            height: 38,
            borderRadius: 6,
            border: 'none',
            background: accentColor,
            color: '#fff',
            padding: '0 12px',
            fontSize: 13,
            fontWeight: 600,
            opacity: draft.trim().length === 0 || isInputBlocked ? 0.55 : 1,
            cursor: draft.trim().length === 0 || isInputBlocked ? 'default' : 'pointer',
          }}
        >
          {labels.textChatSend}
        </button>
      </form>
    </section>
  );
}

function resolveBackground(config: AgentPublicConfig): Pick<CSSProperties, 'background'> {
  if (config.backgroundKind === 'color') {
    return { background: config.backgroundValue || '#000' };
  }
  // backgroundKind === 'image' — backend resolves the URL into backgroundValue.
  // Fall back to solid black if the value is missing/empty.
  if (config.backgroundValue) {
    return { background: `#000 url(${config.backgroundValue}) center/cover no-repeat` };
  }
  return { background: '#000' };
}
